import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, Upload, Download, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const color = (er, max) => (er == null ? "#F1F5F9" : `rgba(232,23,58,${0.15 + 0.85 * (er / (max || 1))})`);

export const BestTimes = ({ clientId }) => {
  const [canale, setCanale] = useState("instagram");
  const [d, setD] = useState(null);
  const [res, setRes] = useState(null);
  const file = useRef(null);
  const load = (refresh) => api.get(`/admin-ai/clients/${clientId}/orari`, { params: { canale, refresh: refresh ? 1 : undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setD(null); load(); }, [clientId, canale]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/admin-ai/clients/${clientId}/metriche/import`, fd); setRes(r.data); toast.success(`${r.data.abbinati} righe abbinate`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const template = async () => {
    const r = await api.get("/admin-ai/metriche/modello.csv", { responseType: "blob" });
    const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "modello-metriche.csv"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 3000);
  };
  const max = d ? Math.max(0, ...d.heatmap.flat().map((c) => c.er || 0)) : 0;
  return (
    <details className="mb-4 rounded-2xl border border-[#5FB3EA] bg-[#A6DAFC]/60 p-4" data-testid="best-times">
      <summary className="cursor-pointer select-none font-display text-lg font-extrabold text-[#0A1128] min-h-[32px]" data-testid="best-times-toggle"><Clock className="inline h-4 w-4 mr-1 text-[#E8173A]" />Orari migliori <span className="text-xs font-bold text-[#E8173A]">Solo admin</span></summary>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={canale} onValueChange={setCanale}><SelectTrigger className="w-[160px] rounded-xl bg-white" data-testid="best-times-canale"><SelectValue /></SelectTrigger>
          <SelectContent>{["instagram", "facebook", "tutti"].map((c) => <SelectItem key={c} value={c}>{c === "tutti" ? "Tutti i canali" : c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent></Select>
        <Button variant="outline" size="sm" onClick={() => file.current?.click()} className="rounded-full font-bold" data-testid="best-times-import"><Upload className="h-4 w-4 mr-1" />Importa metriche CSV</Button>
        <Button variant="ghost" size="sm" onClick={template} className="rounded-full font-bold" data-testid="best-times-template"><Download className="h-4 w-4 mr-1" />Modello CSV</Button>
        <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="best-times-file" />
      </div>
      {res && <div className="mt-2 text-xs text-slate-700" data-testid="best-times-import-result">Abbinate {res.abbinati} righe{res.non_abbinati.length ? ` · non abbinate: ${res.non_abbinati.slice(0, 5).map((x) => `riga ${x.riga} (${x.motivo})`).join("; ")}` : ""}</div>}
      {d && (
        <div className="mt-3 space-y-3">
          <div className="rounded-xl bg-white/85 border border-[#5FB3EA] p-3" data-testid="best-times-top">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <b className="text-[#0A1128]">Orari consigliati</b>
              {d.fonte === "stima" ? <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-bold text-amber-900" data-testid="best-times-stima">stima di settore (pochi dati: {d.n}/{d.soglia} post con metriche)</span>
                : <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="best-times-dati">dai tuoi dati · {d.n} post</span>}
            </div>
            <ol className="mt-2 space-y-1 text-sm">
              {d.top.map((t, i) => <li key={i} data-testid={`best-times-slot-${i}`}><b>{i + 1}. {GIORNI[t.giorno]} {t.ora}</b>{t.fascia ? ` (fascia ${t.fascia})` : ""}{t.er != null ? ` · ER medio ${t.er}% · ${t.n} post` : ""}{t.motivo ? ` · ${t.motivo}` : ""}</li>)}
              {!d.top.length && <li className="text-slate-600">Nessun suggerimento disponibile.</li>}
            </ol>
            {d.fonte === "stima" && <Button variant="ghost" size="sm" onClick={() => load(true)} className="mt-1 rounded-full text-xs font-bold" data-testid="best-times-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Rigenera stima</Button>}
          </div>
          <div className="overflow-x-auto" data-testid="best-times-heatmap">
            <table className="w-full min-w-[520px] border-separate border-spacing-1 text-[11px]">
              <thead><tr><th />{d.fasce.map((f) => <th key={f} className="font-bold text-slate-600">{f}</th>)}</tr></thead>
              <tbody>{d.heatmap.map((row, g) => (
                <tr key={g}><td className="pr-1 font-bold text-slate-600">{GIORNI[g].slice(0, 3)}</td>
                  {row.map((c, f) => <td key={f} title={c.n ? `${c.n} post · ER ${c.er}%` : "nessun dato"} className="h-8 rounded text-center font-bold text-[#0A1128]" style={{ background: color(c.er, max) }}>{c.n ? `${c.er}%` : ""}</td>)}</tr>
              ))}</tbody>
            </table>
            <p className="mt-1 text-[11px] text-slate-600">Engagement rate = (like + commenti + salvataggi + condivisioni) / copertura. La mappa diventa affidabile da {d.soglia} post con metriche.</p>
          </div>
        </div>
      )}
    </details>
  );
};

export const nextBestSlot = (top, fromLocal) => {
  if (!top?.length) return null;
  const base = fromLocal ? new Date(fromLocal) : new Date();
  for (let i = 0; i < 8; i += 1) {
    const day = new Date(base); day.setDate(base.getDate() + i);
    const wd = (day.getDay() + 6) % 7;
    const t = top.find((x) => x.giorno === wd);
    if (t) { const [h, m] = t.ora.split(":").map(Number); day.setHours(h, m, 0, 0); if (day > new Date()) return day; }
  }
  return null;
};
